import { useEffect, useState, FormEvent } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Alert from '@mui/material/Alert';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useTranslation } from 'react-i18next';
import { parsePriceCents, priceInputValue } from '../../lib/price';
import {
  GOAL_TASK_DURATION_UNITS,
  GOAL_TASK_TYPES,
  type Goal,
  type GoalTask,
  type GoalTaskDurationUnit,
  type GoalTaskType,
} from '../../api/types';
import { GoalTaskTypeIcon } from './GoalTaskTypeIcon';
import { TaskTermAutocomplete } from './TaskTermAutocomplete';

export interface GoalTaskDraft {
  type: GoalTaskType;
  title: string;
  note: string | null;
  price_cents: number | null;
  /** A job's estimated duration; both null for a part or when unknown. */
  duration_value: number | null;
  duration_unit: GoalTaskDurationUnit | null;
  goal_id: number;
}

/** Adding to a goal, or editing (and possibly moving) an existing task. */
export type GoalTaskDialogTarget = { mode: 'create'; goal: Goal } | { mode: 'edit'; task: GoalTask };

interface Props {
  target: GoalTaskDialogTarget | null;
  /** Every goal of the car — an edited task can be moved to any of them. */
  goals: Goal[];
  onClose: () => void;
  onSubmit: (target: GoalTaskDialogTarget, draft: GoalTaskDraft) => Promise<void>;
}

/**
 * Popup for a goal task: something to buy or something to do, with an
 * optional estimated price.
 */
export function GoalTaskDialog({ target, goals, onClose, onSubmit }: Props) {
  const { t } = useTranslation();
  const [type, setType] = useState<GoalTaskType>('buy');
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [note, setNote] = useState('');
  const [durationValue, setDurationValue] = useState('');
  const [durationUnit, setDurationUnit] = useState<GoalTaskDurationUnit>('day');
  const [goalId, setGoalId] = useState<number | ''>('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!target) return;
    const task = target.mode === 'edit' ? target.task : null;
    setType(task?.type ?? 'buy');
    setTitle(task?.title ?? '');
    setPrice(priceInputValue(task?.price_cents ?? null));
    setNote(task?.note ?? '');
    setDurationValue(task?.duration_value != null ? String(task.duration_value) : '');
    setDurationUnit(task?.duration_unit ?? 'day');
    setGoalId(task ? task.goal_id : target.mode === 'create' ? target.goal.id : '');
    setError('');
  }, [target]);

  const priceCents = parsePriceCents(price);
  // A whole number of days/weeks/months, or blank for "no estimate".
  const duration = durationValue.trim() === '' ? null : Number(durationValue);
  const durationInvalid = duration !== null && (!Number.isInteger(duration) || duration < 1);
  const invalid = priceCents === undefined || (type === 'do' && durationInvalid);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!target || goalId === '' || invalid) return;
    const isJob = type === 'do' && duration !== null;
    setError('');
    setSubmitting(true);
    try {
      await onSubmit(target, {
        type,
        title,
        note: note || null,
        price_cents: priceCents ?? null,
        duration_value: isJob ? duration : null,
        duration_unit: isJob ? durationUnit : null,
        goal_id: goalId,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('garageCar.goals.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={target !== null} onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {target?.mode === 'edit'
            ? t('garageCar.goals.tasks.editTitle')
            : t('garageCar.goals.tasks.createTitle', {
                goal: target?.mode === 'create' ? target.goal.title : '',
              })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <ToggleButtonGroup
              exclusive
              fullWidth
              color="primary"
              value={type}
              onChange={(_, next: GoalTaskType | null) => next && setType(next)}
              aria-label={t('garageCar.goals.tasks.typeLabel')}
            >
              {GOAL_TASK_TYPES.map((tp) => (
                <ToggleButton key={tp} value={tp}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GoalTaskTypeIcon type={tp} />
                    <span>{t(`garageCar.goals.tasks.type.${tp}`)}</span>
                  </Stack>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <TaskTermAutocomplete
              type={type}
              value={title}
              onChange={setTitle}
              label={t(`garageCar.goals.tasks.title.${type}`)}
              placeholder={t(`garageCar.goals.tasks.titlePlaceholder.${type}`)}
              required
              autoFocus
            />

            <TextField
              label={t('garageCar.goals.tasks.price')}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              error={priceCents === undefined}
              helperText={
                priceCents === undefined
                  ? t('garageCar.goals.tasks.priceInvalid')
                  : t('garageCar.goals.tasks.priceHint')
              }
              inputProps={{ inputMode: 'decimal' }}
              InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }}
              fullWidth
            />

            {type === 'do' && (
              <Stack direction="row" spacing={2}>
                <TextField
                  label={t('garageCar.goals.tasks.duration')}
                  value={durationValue}
                  onChange={(e) => setDurationValue(e.target.value)}
                  error={durationInvalid}
                  helperText={
                    durationInvalid
                      ? t('garageCar.goals.tasks.durationInvalid')
                      : t('garageCar.goals.tasks.durationHint')
                  }
                  inputProps={{ inputMode: 'numeric' }}
                  fullWidth
                />
                <FormControl sx={{ minWidth: 140 }}>
                  <InputLabel id="goal-task-duration-unit-label">
                    {t('garageCar.goals.tasks.durationUnit')}
                  </InputLabel>
                  <Select
                    labelId="goal-task-duration-unit-label"
                    label={t('garageCar.goals.tasks.durationUnit')}
                    value={durationUnit}
                    onChange={(e) => setDurationUnit(e.target.value as GoalTaskDurationUnit)}
                  >
                    {GOAL_TASK_DURATION_UNITS.map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {t(`garageCar.goals.duration.unit.${unit}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            )}

            <TextField
              label={t('garageCar.goals.tasks.note')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />

            {target?.mode === 'edit' && (
              <FormControl fullWidth>
                <InputLabel id="goal-task-goal-label">{t('garageCar.goals.tasks.goal')}</InputLabel>
                <Select
                  labelId="goal-task-goal-label"
                  label={t('garageCar.goals.tasks.goal')}
                  value={goalId}
                  onChange={(e) => setGoalId(Number(e.target.value))}
                >
                  {goals.map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.status === 'active'
                        ? g.title
                        : `${g.title} (${t(`garageCar.goals.status.${g.status}`)})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || invalid}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
